import { Button, Card, Empty, Input, Space, Table, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSectionSelect } from '@/pages/education/components/EducationLookupSelects';

type RegisterRow = {
  id: string;
  section_id: string;
  section_name?: string;
  attendance_date: string;
  period_code: string;
  status: string;
};

function statusColor(status: string): string {
  switch (status) {
    case 'submitted':
    case 'locked':
    case 'complete':
      return 'green';
    case 'draft':
    case 'open':
      return 'orange';
    default:
      return 'default';
  }
}

export default function AttendanceRegisterPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const section = params.get('section') || undefined;
  const date = params.get('date') || undefined;
  const status = params.get('status') || undefined;

  const query = useQuery({
    queryKey: ['education-attendance', section, date, status],
    queryFn: () =>
      api.get<RegisterRow[]>('/education/attendance/registers', {
        params: { section_id: section, attendance_date: date, status },
      }),
  });

  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const rows = query.data?.data ?? [];

  return (
    <EducationPageChrome
      title={t('attendanceTitle')}
      description={t('attendanceIntro')}
      tags={<Tag>{rows.length} {t('attendanceTitle')}</Tag>}
      extra={
        <Space wrap>
          <Button onClick={() => navigate('/education/attendance/overview')}>{t('attendanceOverview')}</Button>
          <Button onClick={() => navigate('/education/attendance/exceptions')}>{t('attendanceExceptions')}</Button>
          <Button onClick={() => navigate('/education/attendance/settings')}>{t('attendanceSettings')}</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`/education/attendance/take?${params.toString()}`)}>
            {t('takeAttendance')}
          </Button>
        </Space>
      }
    >
      <Card size="small">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <EducationSectionSelect
              allowClear
              placeholder={t('section')}
              value={section}
              onChange={(value) => setFilter('section', value || undefined)}
              style={{ width: 240 }}
            />
            <Input
              allowClear
              placeholder={t('attendanceDate')}
              value={date}
              onChange={(event) => setFilter('date', event.target.value || undefined)}
              style={{ width: 160 }}
            />
            <Input
              allowClear
              placeholder={t('status')}
              value={status}
              onChange={(event) => setFilter('status', event.target.value || undefined)}
              style={{ width: 140 }}
            />
          </Space>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={rows}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            locale={{
              emptyText: (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('attendanceEmpty', { defaultValue: 'No attendance registers' })}>
                  <Button type="primary" onClick={() => navigate(`/education/attendance/take?${params.toString()}`)}>
                    {t('takeAttendance')}
                  </Button>
                </Empty>
              ),
            }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/attendance/${row.id}?${params.toString()}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('attendanceDate'), dataIndex: 'attendance_date', width: 140 },
              {
                title: t('section'),
                key: 'section',
                ellipsis: true,
                render: (_: unknown, row: RegisterRow) => row.section_name || row.section_id,
              },
              { title: t('period'), dataIndex: 'period_code', width: 120 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 140,
                render: (value: string) => <Tag color={statusColor(value)}>{value}</Tag>,
              },
            ]}
          />
        </Space>
      </Card>
    </EducationPageChrome>
  );
}
